import { Building, Check, ChevronsUpDown, Globe, Shield, Plus, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useToast } from "@/components/ui/use-toast";
import { observer } from "mobx-react-lite";
import { cn } from "@/lib/utils";

export const CombinedMenuButton = observer(function CombinedMenuButton() {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const { currentOrganization, userOrganizations, setCurrentOrganization, isLoading, refetch } =
    useOrganizationContext();
  const permissions = usePermissions();
  const orgStore = useOrganizationStore();
  const authStore = useAuthStore();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  // Watch for newly created organization to become available
  useEffect(() => {
    if (pendingOrgId && userOrganizations.length > 0) {
      const newOrg = userOrganizations.find((org) => org.id === pendingOrgId);
      if (newOrg) {
        setCurrentOrganization(newOrg);
        setPendingOrgId(null);
      }
    }
  }, [pendingOrgId, userOrganizations, setCurrentOrganization]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error signing out:", error);
      }
    }
  };

  const handleLanguageChange = (newLanguage: "en" | "et") => {
    setLanguage(newLanguage);
  };

  const handleOrgChange = (orgId: string) => {
    const selectedOrg = userOrganizations.find((org) => org.id === orgId);
    if (selectedOrg) {
      setCurrentOrganization(selectedOrg);
      navigate("/buildings");
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgName.trim() || orgName.trim().length < 2) {
      toast({
        variant: "destructive",
        title: t?.common?.invalidName ?? "Invalid name",
        description:
          t?.common?.organizationNameMinLength ??
          "Organisation name must be at least 2 characters long.",
      });
      return;
    }

    const userId = authStore.userProfileId;
    if (!userId) {
      toast({
        variant: "destructive",
        title: t?.common?.error ?? "Error",
        description: t?.common?.userNotFound ?? "User not found. Please try logging in again.",
      });
      return;
    }

    setIsCreating(true);
    try {
      const result = await orgStore.createOrganization(orgName.trim(), userId);

      if (result.success && result.orgId) {
        // Refresh auth session to update JWT claims
        await authStore.loadSession();

        // Set pending org ID - useEffect will watch for it and set it as current
        setPendingOrgId(result.orgId);

        // Refresh organizations list
        await refetch();

        setIsDialogOpen(false);
        setOrgName("");

        toast({
          title: t?.common?.success ?? "Success",
          description: (
            t?.common?.organizationCreatedSuccess ?? 'Organisation "{name}" created successfully.'
          ).replace("{name}", orgName.trim()),
        });
      } else {
        throw new Error(orgStore.error || "Failed to create organisation");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t?.common?.error ?? "Error",
        description: error instanceof Error ? error.message : "Failed to create organisation",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Show loading state while context is initializing
  if (isLoading) {
    return (
      <Button variant="outline" className="min-w-[200px] justify-between" disabled>
        <div className="flex items-center">
          <Building className="mr-2 h-4 w-4 shrink-0" />
          {t.common.loading}...
        </div>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="min-w-[200px] justify-between">
            <div className="flex items-center">
              <Building className="mr-2 h-4 w-4 shrink-0" />
              {currentOrganization?.name || t.common.selectOrganization}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[250px]">
          {/* Organization Switcher Section */}
          <DropdownMenuGroup>
            {userOrganizations.map((org) => (
              <DropdownMenuItem key={org.id} onClick={() => handleOrgChange(org.id)}>
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    currentOrganization?.id === org.id ? "opacity-100" : "opacity-0"
                  )}
                />
                {org.name}
              </DropdownMenuItem>
            ))}
            {permissions.isPlatformAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t?.common?.createNewOrganization ?? "Create New Organisation"}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Settings & Navigation Section */}
          <DropdownMenuGroup>
            {permissions.isAdmin && (
              <DropdownMenuItem onClick={() => navigate("/organization-settings")}>
                {t?.common?.organizationSettings ?? "Manage organisation"}
              </DropdownMenuItem>
            )}
            {permissions.isPlatformAdmin && (
              <DropdownMenuItem onClick={() => navigate("/admin/data-management")}>
                <Shield className="mr-2 h-4 w-4" />
                {t?.common?.adminDashboard ?? "Admin Panel"}
              </DropdownMenuItem>
            )}
            {/* The /tools HUB stays Franklin-staff-only, deliberately, even
                though Drawing Compare — its only section today — is now a
                customer feature reachable at /buildings/:id/drawings. Gating
                the hub on a customer permission would make the next internal
                tool added here customer-visible by default. */}
            {permissions.isPlatformAdmin && (
              <DropdownMenuItem onClick={() => navigate("/tools")}>
                <Wrench className="mr-2 h-4 w-4" />
                {t?.tools?.title ?? "Tools"}
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          {/* Language Section */}
          <DropdownMenuLabel className="flex items-center">
            <Globe className="mr-2 h-4 w-4" />
            {t?.common?.language ?? "Language"}
          </DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => handleLanguageChange("en")}>
              <Check
                className={cn("mr-2 h-4 w-4", language === "en" ? "opacity-100" : "opacity-0")}
              />
              {t.settings.languageEnglish}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleLanguageChange("et")}>
              <Check
                className={cn("mr-2 h-4 w-4", language === "et" ? "opacity-100" : "opacity-0")}
              />
              {t.settings.languageEstonian}
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/settings")}>
            {t?.common?.userSettings ?? "My settings"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout}>
            {t?.common?.logout ?? "Logout"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Organization Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setOrgName("");
            setPendingOrgId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t?.common?.createOrgDialogTitle ?? "Create New Organisation"}
            </DialogTitle>
            <DialogDescription>
              {t?.common?.createOrgDialogDescription ??
                "Enter a name for your new organization. This name can be changed later."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">
                  {t?.common?.organizationNameLabel ?? "Organisation Name"}
                </Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t?.common?.organizationNamePlaceholder ?? "Enter organisation name"}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setOrgName("");
                  setPendingOrgId(null);
                }}
                disabled={isCreating}
              >
                {t?.common?.cancel ?? "Cancel"}
              </Button>
              <Button type="submit" disabled={isCreating || !orgName.trim()}>
                {isCreating
                  ? (t?.common?.creating ?? "Creating...")
                  : (t?.common?.create ?? "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
});
